/* Cart v1.0 25/01/2025 */

import {writable, derived, get} from 'svelte/store';
import {closeOrderItem, updateOrderItemData} from "../core/screens-manager.js";
import {reachGoal} from "../core/ecommerce.js";
import {eventBus} from "../core/event-bus-store.js";
import {menuStore, publishConfigStore} from "../core/app-store.js";
import {getMenuId} from "../core/get-menu-id.js";
import {closeEditDetailsHalfScreen} from "../details/details-store.js";
import {canAddToCart} from "../core/stocks-store.js";

/**
 * {
 *  id,
 *  name,
 *  ref, - reference tenant (optional)
 *  img,
 *  price,
 *  weight,
 *  count
 * }
 *
 * @type {Writable<{ id, name, ref, img, price, weight, count}[]>}
 */
export const cartItemsStore = writable([]);

/**
 * {
 *  count: number,
 *  sum: number
 * }
 *
 * @type {Readable<{count: number, sum: number}>}
 */
export const cartTotalStore = derived(cartItemsStore, (items) => {

    const total = {
        count: 0,
        sum: 0
    };

    (items || []).forEach(item => {

        const sum = (item.price || 0) * item.count;
        total.sum += Math.round(sum * 100); /// multiply 100 to support floats
        total.count += item.count;
    });

    total.sum = total.sum / 100; /// divide by 100 to support floats

    return total;
})

/**
 *
 * @type {Writable<*[]>}
 */
export const ordersStore = writable([]);

/**
 *
 * @type {Writable<{orderData: string}>}
 */
export const cartInfoStore = writable({orderData: null});


cartItemsStore.subscribe(cartItems => {
    if (cartItems && cartItems.length > 0) {
        saveCart(cartItems);
    }

    cartInfoStore.update(cartInfo => {
        cartInfo.orderData = undefined;
        return cartInfo;
    });
})

const getModsDescription = (mods) => (mods || []).map(m => m.map(x => x.name).join(' + ')).join(' + ');

const findItemPredicate = (item, record, priceIndex, modsDescription) => {

    return item.id === record.id &&
        item.priceIndex === priceIndex &&
        item.modsDescription === modsDescription;
}

export const addToCartHandler = (record) => {

    if ((record.prices || []).length > 0) {

        const index = 0;
        const price = record.prices[index];

        if ((record._orderedItems || 0) === 0) {
            addToCart(record, price, index);
        }
        else {
            incrementItemInCart(record, price, index);
        }
    }
}

export const addToCartWithPriceHandler = (data) => {

    const {record, priceIndex} = data;
    if ((record.prices || []).length > 0) {

        const price = record.prices[priceIndex];
        if ((price._count || 0) === 0) {
            addToCart(record, price, priceIndex);
        }
        else {
            incrementItemInCart(record, price, priceIndex);
        }
    }
}

export const addToCart = (record, price, priceIndex, mods = []) => {

    if (!canAddToCart(record.id, 1)) {
        console.log('Cannot add product to card', record);
        return;
    }

    cartItemsStore.update(items => {

        const modsDescription = getModsDescription(mods);
        const found = (items || []).findIndex(i => findItemPredicate(i, record, priceIndex, modsDescription));

        if (found === -1) {
            items.push({
                menuKey: get(menuStore)?.menuKey,
                id: record.id,
                priceIndex: priceIndex,
                name: record.name,
                ref: (record.media || []).filter(x => x.type === 'photo')[0]?.ref,
                img: (record.media || []).filter(x => x.type === 'photo')[0]?.name,
                count: 1,
                price: price.price,
                weight: price.weight,
                mods,
                modsDescription
            });
        }
        else {
            items[found].count++;
        }

        reachGoal('ADD_TO_CART');

        return items;
    });

    eventBus.set([{event: 'addToCart', payload: {record, price, priceIndex, modificators: mods}}]);
}

export const removeFromCartHandler = (record) => {
    if ((record.prices || []).length > 0) {
        const index = 0;
        const price = record.prices[index];
        decrementItemInCart(record, price, index);
    }
}

export const removeFromCartWithPriceHandler = (data) => {
    const {record, priceIndex} = data;
    const price = record.prices[priceIndex];
    decrementItemInCart(record, price, priceIndex, record.mods);
}

export const removeFromCart = (record, price, priceIndex, mods = []) => {

    cartItemsStore.update(items => {

        const modsDescription = getModsDescription(mods);
        items = (items || []).filter(i => findItemPredicate(i, record, priceIndex, modsDescription));

        if (items.length === 0) {
            saveCart(items);
        }

        return items;
    });

    eventBus.set([{event: 'removeFromCart', payload: {record, price, priceIndex, modificators: mods}}]);
}

export const incrementItemInCart = (record, price, priceIndex, mods = []) => {

    cartItemsStore.update(items => {

        const modsDescription = getModsDescription(mods);

        (items || []).forEach(i => {
            if (findItemPredicate(i, record, priceIndex, modsDescription)) {

                if (!canAddToCart(record.id, 1)) {
                    console.log('Cannot add product to card', record);
                    return;
                }

                i.count += 1;
                updateOrderItemData({record, priceIndex});
            }
        })

        return items;
    });

    eventBus.set([{event: 'incrementItemInCart', payload: {record, price, priceIndex, modificators: mods}}]);
}

export const decrementItemInCart = (record, price, priceIndex, mods = []) => {

    cartItemsStore.update(items => {

        const modsDescription = getModsDescription(mods);
        let idxToRemove = null;

        (items || []).forEach( (i,idx) => {

            if (findItemPredicate(i, record, priceIndex, modsDescription)) {
                i.count -= 1;
                if (i.count === 0) {
                    idxToRemove = idx;
                    closeOrderItem();
                    closeEditDetailsHalfScreen();
                }
                else {
                    updateOrderItemData({record, priceIndex});
                }
            }
        });

        if (idxToRemove !== null) {
            items.splice(idxToRemove, 1);
        }

        if (items.length === 0) {
            saveCart(items);
            eventBus.set([{event: 'closeEditDetailsHalfScreen', payload: {}}]);
        }

        return items;
    });

    eventBus.set([{event: 'decrementItemInCart', payload: {record, price, priceIndex, modificators: mods}}]);
}

export const loadCart = () => {

    const cartItemsStr = window.localStorage.getItem(getCartKey());
    try {
        const cartItems = JSON.parse(cartItemsStr);
        cartItemsStore.set(cartItems || []);
    }
    catch (e) {
        console.error('Load cart error', e);
    }
}

export const clearCart = () => {
    cartItemsStore.set([]);
    saveCart([]);
}

const saveCart = (items) => {
    window.localStorage.setItem(getCartKey(), JSON.stringify(items));
}

let _cachedCartKey = undefined;
const getCartKey = () => {

    if (_cachedCartKey === undefined) {
        const cartId = getMenuId( get(publishConfigStore) );
        _cachedCartKey = `dm_cart_${ cartId }`;
    }

    return _cachedCartKey;
}

export const confirmOrder = (cartItems) => {

    ordersStore.update(orders => {

        const orderData = [];
        const total = {};
        cartItems.forEach(item => {
            total.sum += (item.price * item.count);
            total.count += item.count;
            orderData.push([item.id, item.priceIndex, item.count].join('-'));
        });

        cartInfoStore.update(cartInfo => {
            cartInfo.orderData = btoa(orderData.join('!'));
            return cartInfo;
        });


        orders.push({
            date: Date.now(),
            cartItems,
            totalSum: total.sum,
            totalCount: total.count
        });

        return orders;
    })
}
